import type {
    JSTPushAuthorizationResult,
    JSTPushAuthReengagementModalResult,
    JSTPushModalConfig,
    JSTPushModalPresentationType,
    JSTPushPromptStatusResult
} from '../types/JSTPushAuthorization';

import TypedNativeModules from '../internal/TypedNativeModules';

/**
 * Checks both whether a push prompt (primer or re-engagement modal) can be shown based on rules and cooldown criteria AND the current push authorization status.
 *
 * @returns A JSTPushPromptStatusResult. The success result indicates both `canShowPushPrompt` and current JSTPushAuthorizationStatus.
 */
export async function canShowPushPrompt(): Promise<JSTPushPromptStatusResult | null> {
    return (await TypedNativeModules.RNJSTPushAuthorization?.canShowPushPrompt?.()) ?? null;
}

/**
 * Retrieves the current push authorization status.
 *
 * @returns The JSTPushAuthorizationResult that will contain an error or current JSTPushAuthorizationStatus.
 */
export async function getPushAuthorizationStatus(): Promise<JSTPushAuthorizationResult | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.getPushAuthorizationStatus?.()) ?? null
    );
}

/**
 * Requests system authorization, possibly gated by a push primer modal. This call should be used for any programmatic (non user-driven) push authorization requests or re-engagement requests.
 *
 * Flow:
 * 1. If the current push authorization status is .authorized, .denied, or .ephemeral, then this method will return the current status without presenting or showing any UI to the user.
 * 2. If the current push authorization status is .notDetermined or .provisional AND the user has not met the rules required to be registered or is within a cooldown period (aka "canShowPushPrompt() == false"), this method will return the current status (.notDetermined or .provisional).
 * 3. If the current push authorization status is .notDetermined or .provisional AND the user meets requirements to show the push primer ("canShowPushPrompt() == true"), then the user will be shown the modal push primer.
 *    - If the user snoozes the push primer, this method will return the current push authorization status.
 *    - If the user proceeds to the system push authorization alert, then this method will return their current push authorization status *after* closing the system alert.
 *
 * @param presentationType - The presentation method for the push primer modal.
 * @param from - An optional string for logging the referrer.
 * @param customModalConfig - An optional modal configuration. If provided, values from the customModalConfig can modify the default push primer layout modals.
 * @returns The JSTPushAuthorizationResult that will contain an error or JSTPushAuthorizationStatus.
 */
export async function requestSystemPushAuthWithPrimer(
    presentationType: JSTPushModalPresentationType,
    from?: string | null,
    customModalConfig?: JSTPushModalConfig | null,
): Promise<JSTPushAuthorizationResult | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.requestSystemPushAuthWithPrimer?.(
            presentationType,
            from,
            customModalConfig,
        )) ?? null
    );
}

/**
 * Immediately requests push authorization from the OS. This may trigger the system authorization alert. This method first checks current push authorization status. If the status is NOT one of .notDetermined or .provisional, the current status will be returned without triggering the system authorization alert. Otherwise, the result of the system authorization alert (.authorized or .denied) will be returned.
 *
 * **WARNING!** Because the system authorization alert can only be shown once on iOS, you should only call this function from inline UI such as a banner - never intrusive modal UI.
 *
 * @param from - An optional string for logging the referrer.
 * @returns The JSTPushAuthorizationResult that will contain an error or JSTPushAuthorizationStatus.
 */
export async function requestImmediateSystemPushAuthorization(
    from?: string | null,
): Promise<JSTPushAuthorizationResult | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.requestImmediateSystemPushAuthorization?.(
            from,
        )) ?? null
    );
}

/**
 * This request triggers a re-engagement popup modal if both the current authorization status is .denied and the user meets push prompt criteria.
 *
 * Flow:
 * 1. If the current push authorization status is NOT .denied, then the .canceledAuthState result is returned without presenting or showing any UI to the user.
 * 2. If the current push authorization status is .denied AND the user DOES NOT meet requirements to show the re-engagement modal ("canShowPushPrompt() == false"), then the .canceledRules result is returned without presenting or showing any UI to the user.
 * 3. If the current push authorization status is .denied AND the user meets requirements to show the re-engagement modal ("canShowPushPrompt() == true"), then the user will be shown the re-engagement modal.
 *    - If the user snoozes the re-engagement modal, this method will return .snooze.
 *    - If the user taps "Allow Push" on the re-engagement modal, then this method will open the system push settings and return .allowNotifications.
 *
 * @param presentationType - The presentation method for the push primer modal.
 * @param from - An optional string for logging the referrer.
 * @param customModalConfig - An optional re-engagement modal configuration. If provided, values from the customModalConfig can modify the default re-engagement modal.
 * @returns The JSTPushAuthReengagementModalResult that will contain the result of the re-engagement modal interaction.
 */
export async function requestReengagementModal(
    presentationType: JSTPushModalPresentationType,
    from?: string | null,
    customModalConfig?: JSTPushModalConfig | null,
): Promise<JSTPushAuthReengagementModalResult | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.requestReengagementModal?.(
            presentationType,
            from,
            customModalConfig,
        )) ?? null
    );
}

/**
 * Opens the app push notification settings in system UI.
 *
 * @returns The JSTPushAuthorizationResult indicating success or error.
 */
export async function openPushNotificationAuthorizationSettings(): Promise<JSTPushAuthorizationResult | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.openPushNotificationAuthorizationSettings?.()) ??
        null
    );
}

/**
 * Sets the push primer cooldown timeout to "now"+timeout. Note that timeouts for push primer modal and re-engagement modals all use a single prompt cooldown period.
 */
export async function resetPushPromptCooldownTimeout(): Promise<void | null> {
    return (
        (await TypedNativeModules.RNJSTPushAuthorization?.resetPushPromptCooldownTimeout?.()) ??
        null
    );
}

/**
 * Signifies to push auth tracker that a significant event has occurred.
 */
export async function incrementSignificantEvent(): Promise<void | null> {
    return (await TypedNativeModules.RNJSTPushAuthorization?.incrementSignificantEvent?.()) ?? null;
}
